import React, {
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Opportunity,
  OpportunitySEStatus,
  OpportunitySETag,
  OpportunityUpdate,
} from '../../types/opportunity';
import {
  getOpportunityStatusColor,
  setSENotRequired,
  tagUserToOpportunity,
  untagUserToOpportunity,
} from '../../support/opportunityHelpers';
import { patchItem } from '../../support/queries';
import OppTableColumnManagerModal from '../Common/OppTableColumnManagerModal';
import { TemporaryUserView, UserView, UserViewType } from '../../types/user';
import { defaultColumns, defaultOrderBy } from '../../support/config/tables';
import { useUserRegistry } from '../../hooks/useUserRegistry';
import {
  HbrButton,
  HbrCheckbox,
  HbrDomTable,
  HbrIcon,
  HbrPagination,
  HbrInput,
  HbrSwitch,
} from '@harbor/elements-react-standalone';
import {
  Column,
  ColumnAlignment,
  ColumnPin,
  ColumnType,
  getColumnTypeConfig,
} from '../../types/table';
import StbTableFilters from '../Common/TableView/StbTableFilters';
import OppDatePresetSelect, { SelectedDatePreset } from './OppDatePresetSelect';
import { PHASE_COLOURS } from '../../support/config/tables';
import {
  magnifyingGlass,
  navUsers,
  statusInfoSquare,
  statusWarning,
} from '@harbor/elements/icons';
import { RootState, useAppDispatch } from '../../support/store';
import { useSelector } from 'react-redux';
import {
  fetchSalesforceOpportunities,
  fetchTaggedOpportunities,
  setDatePreset,
  setNeedsRefresh,
  setPage,
  setSearch,
  setSize,
  tagOpportunity,
  untagOpportunity,
} from '../../support/slices/salesforceOpportunitiesSlice';
import StbLoader from '../security-toolbox-ui/StbLoader';
import OpportunityStatusModal from './OpportunityStatusModal';
import { updateSENote } from '../../support/salesForceHelpers';
import TABLE_STYLES from '../Common/TableView/table-styles';
import { CellContainer } from '../Common/TableView/cell-templates';
import cellTemplate from '../Common/TableView/cell-templates';
import thTemplate from '../Common/TableView/th-templates';
import { SfRecordType } from '../../types/salesforce';
import StbModal from '../security-toolbox-ui/StbModal';
import { asSingleValue, formatSEPhase } from '../../support/utils';

interface OppTableProps {
  view?: UserView | TemporaryUserView;
  isCustomerView?: boolean;
  isIncubationView?: boolean;
}

const TABLE_RIGHT_BUFFER = 10;
const TABLE_WIDTH_SAFETY_BUFFER = 2;
const TABLE_SCROLL_ACTIVATION_THRESHOLD = 40;

const getColumnWidth = (column: Column) => {
  if (typeof column.width === 'number') return column.width;
  if (typeof column.minWidth === 'number') return column.minWidth;

  switch (column.id) {
    case 'selected':
      return 40;
    case 'SE_Status__c':
      return 170;
    case 'Name':
      return 320;
    default:
      return 160;
  }
};

const getNaturalTableWidth = (columns: Column[]) => {
  return columns.reduce((total, column) => total + getColumnWidth(column), 0);
};

const OppTable: React.FC<OppTableProps> = ({
                                             view,
                                             isCustomerView = false,
                                             isIncubationView = false,
                                           }) => {
  const {
    updateViewColumn,
    updateViewColumns,
    updateViewOrderBy,
    updateViewUserOwnedOnly,
  } = useUserRegistry();

  const isCustomContext = isCustomerView || isIncubationView;

  const dispatch = useAppDispatch();

  const opportunityData = useSelector(
    (state: RootState) =>
      state.salesforceOpportunities.views[String(view?.id)]?.opportunities
  );
  const opportunitiesInitialized = useSelector(
    (state: RootState) => state.salesforceOpportunities.initialLoadComplete
  );

  const taggedOpportunityIds = useSelector(
    (state: RootState) => state.salesforceOpportunities.taggedOpportunityIds
  );
  const userTaggedOpportunities = useMemo(() => {
    return new Set(taggedOpportunityIds ?? []);
  }, [taggedOpportunityIds]);

  const userTaggedOpportunitiesLoaded = useSelector(
    (state: RootState) => state.salesforceOpportunities.taggedOpportunitiesLoaded
  );
  const taggedOpportunitiesError = useSelector(
    (state: RootState) => state.salesforceOpportunities.taggedOpportunitiesError
  );
  const needsRefresh = useSelector(
    (state: RootState) => state.salesforceOpportunities.needsRefresh
  );

  const opportunities = useMemo(() => {
    return (opportunityData ?? []).map((opportunity) => ({
      ...opportunity,
      isTagged: userTaggedOpportunities.has(opportunity.Id),
    }));
  }, [opportunityData, userTaggedOpportunities]);

  const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);

  const totalCount = useSelector(
    (state: RootState) =>
      state.salesforceOpportunities.views[view?.id]?.totalCount ?? 0
  );
  const pageIndex = useSelector(
    (state: RootState) =>
      state.salesforceOpportunities.views[view?.id]?.page ?? 0
  );
  const size = useSelector(
    (state: RootState) =>
      state.salesforceOpportunities.views[view?.id]?.size ?? 30
  );
  const search = useSelector(
    (state: RootState) =>
      state.salesforceOpportunities.views[view?.id]?.search ?? ''
  );
  const datePresetId = useSelector(
    (state: RootState) =>
      state.salesforceOpportunities.views[view?.id]?.datePreset?.id ?? null
  );
  const viewLoading = useSelector(
    (state: RootState) => state.salesforceOpportunities.loading
  );
  const error = useSelector(
    (state: RootState) => state.salesforceOpportunities.error
  );

  const [maxSelected, setMaxSelected] = useState<boolean>(false);

  const [selectedOpportunityIds, setSelectedOpportunityIds] = useState<Set<string>>(new Set());
  const [selectedOpportunities, setSelectedOpportunities] = useState<Opportunity[]>([]);
  const [selectedOpportunityUpdates, setSelectedOpportunityUpdates] = useState<
    Record<string, OpportunitySEStatus | OpportunitySETag | string>
  >({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [success, setSuccess] = useState<Record<string, boolean>>({});
  const [opportunityUpdateModalOpen, setOpportunityUpdateModalOpen] = useState(false);
  const [multiSelect, setMultiSelect] = useState(false);

  const [columnManagerOpen, setColumnManagerOpen] = useState(false);

  const visibleColumns = useMemo(() => {
    const columns = (view?.columns || defaultColumns.opportunity).map(
      (column) => getColumnTypeConfig(column)
    );
    const order = view?.orderBy ?? defaultOrderBy.opportunity;

    return [
      ...(!isIncubationView
        ? [
          {
            id: 'selected',
            name: '',
            width: 20,
            maxWidth: 20,
            resizable: false,
            columnType: ColumnType.Custom,
            align: ColumnAlignment.Center,
            sortable: false,
            reorderable: false,
            pin: ColumnPin.Left,
          },
          {
            id: 'SE_Status__c',
            name: 'SE Status',
            width: 150,
            columnType: ColumnType.Custom,
            sortable: false,
            reorderable: false,
          },
        ]
        : []),
      {
        id: 'Name',
        name: 'Opportunity',
        width: 300,
        columnType: ColumnType.TitleLink,
        sortable: true,
        order: order?.column === 'Name' ? order.order : undefined,
        reorderable: false,
      },
      ...columns.map((column) => ({
        ...column,
        name: column.name,
        columnType: column.columnType || ColumnType.String,
        order: order?.column === column.id ? order.order : undefined,
      })),
    ] as Column[];
  }, [view?.columns, view?.orderBy, isIncubationView]);

  const pageRef = useRef<HTMLDivElement | null>(null);
  const [pageWidth, setPageWidth] = useState(0);

  useLayoutEffect(() => {
    const element = pageRef.current;
    if (!element) return;

    const getAvailableWidth = () => {
      const appContent = element.closest('.app-content') as HTMLElement | null;

      return (
        appContent?.clientWidth ||
        element.clientWidth ||
        document.documentElement.clientWidth ||
        window.innerWidth ||
        0
      );
    };

    const update = () => {
      setPageWidth(getAvailableWidth());
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);

    const appContent = element.closest('.app-content') as HTMLElement | null;
    if (appContent) {
      observer.observe(appContent);
    }

    window.addEventListener('resize', update, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const tableWidth = useMemo(() => {
    const available = Math.max(
      320,
      pageWidth - TABLE_RIGHT_BUFFER - TABLE_WIDTH_SAFETY_BUFFER
    );

    const natural = getNaturalTableWidth(visibleColumns);

    /*
      Avoid tiny useless horizontal scroll from rounding, Harbor internals,
      borders, and width estimation mismatch.
      Only allow scroll when columns are meaningfully wider than available.
    */
    if (natural <= available + TABLE_SCROLL_ACTIVATION_THRESHOLD) {
      return available;
    }

    return natural;
  }, [pageWidth, visibleColumns]);

  const refreshOpportunities = useCallback(async () => {
    if (!view?.id) return;

    try {
      dispatch(fetchSalesforceOpportunities({ view }));
    } catch (error) {
      console.error('Error fetching Opportunities data:', error);
    }
  }, [dispatch, view]);

  useEffect(() => {
    if (needsRefresh) {
      refreshOpportunities();
      dispatch(setNeedsRefresh(false));
    }
  }, [needsRefresh, refreshOpportunities, dispatch]);

  useEffect(() => {
    if (!view?.id || !userTaggedOpportunitiesLoaded) return;
    refreshOpportunities();
    setSelectedOpportunityIds(new Set());
  }, [view, userTaggedOpportunitiesLoaded, refreshOpportunities]);

  useEffect(() => {
    if (!userTaggedOpportunitiesLoaded) {
      dispatch(fetchTaggedOpportunities());
    }
  }, [userTaggedOpportunitiesLoaded, dispatch]);

  useLayoutEffect(() => {
    if (viewLoading) {
      setIsLoadingOpportunities(true);
    } else {
      const timeout = setTimeout(() => {
        setIsLoadingOpportunities(false);
      }, 250);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [viewLoading]);

  const sanitizeColumns = (columns: unknown[]) => {
    return columns
      .filter(
        (col: any) => !['selected', 'SE_Status__c', 'Name'].includes(col.id)
      )
      .map((col: any) => {
        const cleanedCol = { ...col };
        Object.keys(cleanedCol).forEach((key) => {
          if (typeof cleanedCol[key] === 'function') {
            delete cleanedCol[key];
          }
        });
        return cleanedCol;
      });
  };

  const handleSetVisibleColumns = (selectedColumns: Column[]) => {
    if (typeof view.id === 'number') {
      const updatedColumns = selectedColumns.map((col) => {
        const found = visibleColumns.find((selected) => selected.id === col.id);
        return found ?? getColumnTypeConfig(col);
      });

      updateViewColumns(
        UserViewType.Opportunity,
        view.id,
        sanitizeColumns(updatedColumns)
      );
      setColumnManagerOpen(false);
    }
  };

  const renderHeader = () => {
    return visibleColumns.map((column) => {
      switch (column.id) {
        case 'selected':
          return (
            <div
              key={`column-${column.id}`}
              slot={`column-${column.id}`}
              className="pt-1 w-full"
            >
              <HbrCheckbox
                className="ml-0 pl-0"
                aria={{
                  label: 'Select All',
                }}
                indeterminate={
                  selectedOpportunityIds.size > 0 &&
                  selectedOpportunityIds.size < opportunities.length
                }
                checked={selectedOpportunityIds.size === opportunities.length}
                onClick={(e) => {
                  e.stopPropagation();
                  setMaxSelected(!maxSelected);
                  setSelectedOpportunityIds(
                    maxSelected
                      ? new Set()
                      : new Set(
                        opportunities
                          .filter(
                            (o) =>
                              o.SE_Status__c !==
                              OpportunitySEStatus.TechnicalClose
                          )
                          .map((i) => i.id)
                      )
                  );
                }}
              />
            </div>
          );

        case 'SE_Status__c':
          return (
            <HbrButton
              onClick={(e) => {
                e.stopPropagation();
                setMultiSelect(true);
                handleUpdateSelectedOpportunities(
                  selectedOpportunityIds.size
                    ? selectedOpportunityIds
                    : new Set()
                );
              }}
              key={`column-${column.id}`}
              slot={`column-${column.id}`}
              disabled={!selectedOpportunityIds.size}
              className="-my-1"
              variant="outline"
            >
              <HbrIcon slot="prefix" asset={navUsers} />
              {!selectedOpportunityIds.size
                ? 'SE Status'
                : selectedOpportunityIds.size > 1
                  ? 'Edit Opportunities'
                  : 'Tag SE Status'}
            </HbrButton>
          );

        default:
          return thTemplate(
            column,
            visibleColumns,
            SfRecordType.Opportunity,
            handleSetVisibleColumns,
            !isCustomerView
          );
      }
    });
  };

  const renderBody = () => {
    if (!opportunities || opportunities.length === 0) {
      return null;
    }

    const list = [];
    // untested fix for duplicating row contents on switching view
    // untested because I can't replicate but other users have experienced this bug
    // duplications in the opportunities array is the most likely cause
    const uniqueOpportunitiesMap = new Map<string, Opportunity>();
    opportunities.forEach(opp => uniqueOpportunitiesMap.set(opp.id, opp));
    const uniqueOpportunitiesArray = Array.from(uniqueOpportunitiesMap.values());
    uniqueOpportunitiesArray.forEach((row) => {
      visibleColumns.forEach((column) => {
        switch (column.id) {
          case 'selected':
            list.push(
              <CellContainer
                row={row}
                column={column}
                key={`row-${row.id}-column-${column.id}`}
                className="pt-1"
              >
                <HbrCheckbox
                  aria={{
                    label: 'Select row',
                  }}
                  checked={selectedOpportunityIds.has(row.id)}
                  disabled={
                    row.SE_Status__c === OpportunitySEStatus.TechnicalClose
                  }
                  onHbr-click={() => {
                    const isSelected = selectedOpportunityIds.has(row.id);
                    setSelectedOpportunityIds((prev) => {
                      const newSet = new Set(prev);
                      if (isSelected) {
                        newSet.delete(row.id);
                      } else {
                        newSet.add(row.id);
                      }
                      return newSet;
                    });
                  }}
                />
              </CellContainer>
            );
            break;

          case 'SE_Status__c':
            list.push(renderStatusChip(row));
            break;

          case 'Name':
            list.push(
              cellTemplate(
                { ...column, columnType: ColumnType.TitleLink },
                row,
                { titleLink: `/opportunity/${row.id}` }
              )
            );
            break;

          case 'StageName': {
            const parts = row[column.id]?.split('-');
            const stage0 = parts?.[0]?.trim();
            const stage1 = parts?.[1]?.trim();

            if (!stage0) {
              list.push(row[column.id]);
              break;
            }

            list.push(
              <CellContainer
                row={row}
                column={column}
                key={`row-${row.id}-column-${column.id}`}
                className="min-h-8 justify-start"
              >
                <div
                  className={`flex items-center justify-center min-w-7 min-h-7 rounded-full ${
                    PHASE_COLOURS[stage0]
                  } mr-2 flex-0 font-bold text-lg text-white -my-1`}
                >
                  {stage1 ? stage0 : ''}
                </div>
                <div className="flex-1 whitespace-nowrap">
                  {stage1 ? stage1 : stage0}
                </div>
              </CellContainer>
            );
            break;
          }

          case 'Account.Name':
            list.push(
              cellTemplate(
                { ...column, columnType: ColumnType.TitleLink },
                row,
                { titleLink: `/customer/${row['AccountId']}` }
              )
            );
            break;

          case 'se_phase':
            list.push(
              <CellContainer
                row={row}
                column={column}
                key={`row-${row.id}-column-${column.id}`}
              >
                {formatSEPhase(row.se_phase)}
              </CellContainer>
            );
            break;

          default:
            list.push(cellTemplate(column, row));
        }
      });
    });

    return list;
  };

  const renderStatusChip = (row: Opportunity) => {
    const statusColour = getOpportunityStatusColor(
      row.SE_Status__c as OpportunitySEStatus,
      row.isTagged
    );
    const statusText =
      (row.SE_Status__c as OpportunitySEStatus) ?? OpportunitySEStatus.Unknown;

    return (
      <div
        slot={`row-${row.id}-column-SE_Status__c`}
        key={`row-${row.id}-column-SE_Status__c`}
      >
        <button
          type="button"
          onClick={(e) => {
            setMultiSelect(false);
            if (
              [
                OpportunitySEStatus.Completed,
                OpportunitySEStatus.TechnicalClose,
              ].includes(statusText)
            ) {
              return;
            }
            handleUpdateSelectedOpportunities(new Set([row.id]));
            e.stopPropagation();
          }}
          aria-label={statusText}
          className="w-48 flex items-center text-left px-2 py-2 -my-1 rounded-md bg-white border-2 border-gray-300"
        >
          <span
            className={`rounded-full ${statusColour} text-white w-5 h-5 mr-2 flex items-center justify-center`}
          />
          <span className="font-semibold">{statusText}</span>
        </button>
      </div>
    );
  };

  const handleSearchChange = (value: string) => {
    if (!view?.id) return;
    dispatch(setPage({ viewId: view.id, page: 0 }));
    dispatch(setSearch({ viewId: view.id, search: value }));
    refreshOpportunities();
    setSelectedOpportunityIds(new Set());
  };

  const handleSelectDatePreset = (datePreset: SelectedDatePreset | null) => {
    if (!view?.id) return;
    dispatch(setPage({ viewId: view.id, page: 0 }));
    dispatch(setDatePreset({ viewId: view.id, datePreset }));
    refreshOpportunities();
    setSelectedOpportunityIds(new Set());
  };

  const handleCancelManageColumns = async () => {
    setColumnManagerOpen(false);
  };

  const handleChangePage = (chunk: number) => {
    if (!view?.id) return;
    setSelectedOpportunityIds(new Set());
    dispatch(setPage({ viewId: view.id, page: chunk }));
    refreshOpportunities();
  };

  const handleChangeRowsPerPage = (size: number) => {
    if (!view?.id) return;
    dispatch(setSize({ viewId: view.id, size }));
    refreshOpportunities();
    setSelectedOpportunityIds(new Set());
  };

  const handleSetViewFilters = (_filters: any) => {
    if (!view?.id) return;
    dispatch(setPage({ viewId: view.id, page: 0 }));
  };

  const handleUpdateSelectedOpportunities = (ids: Set<string>) => {
    setSelectedOpportunities(
      opportunities.filter((opportunity) =>
        Array.from(ids).includes(opportunity.Id)
      )
    );

    const initial = opportunities.reduce((acc, opportunity) => {
      acc[opportunity.Id] = false;
      return acc;
    }, {});

    setLoading(initial);
    setSuccess(initial);
    setOpportunityUpdateModalOpen(true);
  };

  const handleSetOpportunityUpdates = (
    update: OpportunityUpdate,
    value: OpportunitySEStatus | OpportunitySETag | string,
    resetUpdates = false
  ) => {
    if (resetUpdates) {
      setSelectedOpportunityUpdates({ [update]: value });
    } else if (!value) {
      const { [update]: _, ...rest } = selectedOpportunityUpdates;
      setSelectedOpportunityUpdates(rest);
    } else {
      setSelectedOpportunityUpdates((prev) => ({ ...prev, [update]: value }));
    }
  };

  const handleCancelOpportunityUpdates = async () => {
    setLoading({});
    setSelectedOpportunityUpdates({});
    setOpportunityUpdateModalOpen(false);
  };

  const handleConfirmOpportunityUpdates = async () => {
    setLoading((prev) => {
      const newLoading = { ...prev };
      Object.keys(newLoading).map((key) => (newLoading[key] = true));
      return newLoading;
    });

    try {
      for (const opportunity of selectedOpportunities) {
        if (
          !opportunity.isTagged &&
          (selectedOpportunityUpdates[OpportunityUpdate.Tag] ===
            OpportunitySETag.TagSE ||
            [
              OpportunitySEStatus.TechnicalOnHold,
              OpportunitySEStatus.WorkInProgress,
            ].includes(
              selectedOpportunityUpdates[
                OpportunityUpdate.Status
                ] as OpportunitySEStatus
            ))
        ) {
          await tagUserToOpportunity(opportunity);
          dispatch(tagOpportunity(opportunity.Id));
        }

        if (
          !(
            selectedOpportunityUpdates[OpportunityUpdate.Tag] ===
            OpportunitySETag.UntagSE
          ) &&
          [
            OpportunitySEStatus.TechnicalOnHold,
            OpportunitySEStatus.WorkInProgress,
          ].includes(
            selectedOpportunityUpdates[
              OpportunityUpdate.Status
              ] as OpportunitySEStatus
          )
        ) {
          await patchItem('sf/opportunity', {
            Id: opportunity.Id,
            SE_Status__c: selectedOpportunityUpdates[OpportunityUpdate.Status],
          });
        } else if (
          selectedOpportunityUpdates[OpportunityUpdate.Status] ===
          OpportunitySEStatus.NotRequired
        ) {
          await setSENotRequired(opportunity);
        }

        if (
          selectedOpportunityUpdates[OpportunityUpdate.Note] &&
          selectedOpportunityUpdates[OpportunityUpdate.Note].trim().length > 0
        ) {
          await updateSENote(
            opportunity,
            `${formatSEPhase(opportunity.se_phase)} - ${selectedOpportunityUpdates[OpportunityUpdate.Note]}`
          );
        }

        if (
          opportunity.isTagged &&
          selectedOpportunityUpdates[OpportunityUpdate.Tag] ===
          OpportunitySETag.UntagSE
        ) {
          await untagUserToOpportunity(opportunity.Id);
          dispatch(untagOpportunity(opportunity.Id));
        }

        setLoading((prev) => ({ ...prev, [opportunity.Id]: false }));
        setSuccess((prev) => ({ ...prev, [opportunity.Id]: true }));
      }
    } catch (error) {
      console.error('Error updating opportunities:', error);
    } finally {
      refreshOpportunities();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setOpportunityUpdateModalOpen(false);
      setSelectedOpportunityUpdates({});
      setLoading({});
      setSuccess({});
    }
  };

  return (
    <div className="salesforce-table-page" ref={pageRef}>
      {!isIncubationView && (
        <div className="salesforce-table-header">
          <div
            className={`${TABLE_STYLES.filtersGrid} ${TABLE_STYLES.filtersOpp} ${
              isCustomContext ? 'bg-white w-full' : ''
            }`}
          >
            <div className="min-w-0 w-full h-full flex items-center">
              {opportunitiesInitialized &&
                (!isCustomerView || opportunities?.length > 0 || search) && (
                  <HbrInput
                    placeholder="Search opportunities"
                    value={search}
                    size="large"
                    clearable={true}
                    onHbr-change={(e) =>
                      handleSearchChange(asSingleValue(e.target.value))
                    }
                    className="ml-0 w-full"
                  >
                    <HbrIcon
                      slot="prefix"
                      name="magnifying-glass"
                      asset={magnifyingGlass}
                      size="16px"
                      staticColors={true}
                    />
                  </HbrInput>
                )}
            </div>

            {!isCustomerView && (
              <div className="flex items-center h-full">
                <HbrSwitch
                  size="small"
                  checked={view?.userOwnedOnly ?? false}
                  className="flex items-center w-full"
                  onHbr-click={(e) => {
                    if (typeof view.id === 'number') {
                      updateViewUserOwnedOnly(
                        UserViewType.Opportunity,
                        view.id,
                        e.target.checked
                      );
                    }
                  }}
                >
                  <div className="font-semibold w-full">My Opportunities</div>
                </HbrSwitch>
              </div>
            )}

            {!isCustomerView && (
              <div className="md:col-span-2 xl:col-span-1 min-w-0 w-full flex flex-col sm:flex-row sm:items-start gap-2">
                <OppDatePresetSelect
                  value={datePresetId}
                  onChange={handleSelectDatePreset}
                />
                <div className="min-w-0 flex-1">
                  <StbTableFilters
                    userView={view as UserView}
                    setViewFilters={handleSetViewFilters}
                    visibleColumns={visibleColumns}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {opportunitiesInitialized &&
        !error &&
        !isLoadingOpportunities &&
        opportunities?.length === 0 && (
          <div className="salesforce-table-header">
            <div className="bg-blue-100 border border-blue-200 text-blue-800 px-4 py-3 rounded-md mb-4 flex items-center gap-2">
              <HbrIcon asset={statusInfoSquare} sentiment="info" />
              No opportunities found
            </div>
          </div>
        )}

      {(error === 'Unauthorized' ||
        taggedOpportunitiesError === 'Unauthorized') && (
        <div className="salesforce-table-header">
          <div className="bg-red-100 border border-red-200 text-red-800 px-4 py-3 rounded-md mb-4 flex items-center gap-2">
            <HbrIcon asset={statusWarning} sentiment="warning" />
            SalesForce session expired. Please log in again.
          </div>
        </div>
      )}

      {opportunities?.length > 0 && (
        <div
          className="salesforce-table-surface"
        >
          <HbrDomTable
            autoLayout={true}
            columns={visibleColumns}
            data={opportunities}
            onHbr-sort={(e) => {
              if (
                ['selected', 'SE Status', 'se_phase'].includes(
                  e?.detail?.column.id
                )
              ) {
                return;
              }

              const { order, column } = e.detail;

              if (view && !isCustomContext && typeof view.id === 'number') {
                updateViewOrderBy(UserViewType.Opportunity, view.id, {
                  column: column.id,
                  order,
                });
              }
            }}
            onHbr-column-reorder={(e) => {
              if (e.detail.columns) {
                if (view && !isCustomContext && typeof view.id === 'number') {
                  updateViewColumns(
                    UserViewType.Opportunity,
                    view.id,
                    sanitizeColumns(e.detail.columns)
                  );
                }
              }
            }}
            onHbr-column-resize={(e) => {
              if (view && !isCustomContext && typeof view.id === 'number') {
                updateViewColumn(
                  UserViewType.Opportunity,
                  view.id,
                  e.detail.column.id,
                  'width',
                  e.detail.column.width
                );
              }
            }}
            className={`${opportunityUpdateModalOpen || columnManagerOpen ? 'z-0' : ''}`}
          >
            {renderHeader()}
            {renderBody()}
          </HbrDomTable>
        </div>
      )}

      <div className="salesforce-table-header">
        <div className={`${isCustomerView ? '' : TABLE_STYLES.pagination} py-2 mb-0`}>
          <HbrPagination
            itemCount={totalCount}
            pageSize={isCustomerView ? 5 : size}
            page={pageIndex}
            sizeChanger={!isCustomerView}
            onHbr-size-changed={(e) =>
              handleChangeRowsPerPage(e.detail.pageSize)
            }
            onHbr-page-changed={(e) => handleChangePage(e.detail)}
          />
        </div>
      </div>

      <StbModal
        title={
          selectedOpportunities.length === 1
            ? 'Update Opportunity'
            : 'Update Selected Opportunities'
        }
        open={opportunityUpdateModalOpen}
        submitLabel={`Update ${
          selectedOpportunities.length === 1 ? 'Opportunity' : 'Opportunities'
        }`}
        role="update"
        onClose={() => handleCancelOpportunityUpdates()}
        onSubmit={() => handleConfirmOpportunityUpdates()}
      >
        <OpportunityStatusModal
          opportunities={selectedOpportunities}
          updates={selectedOpportunityUpdates}
          setOpportunityUpdate={handleSetOpportunityUpdates}
          loading={loading}
          success={success}
          multi={multiSelect}
        />
      </StbModal>

      {columnManagerOpen && (
        <OppTableColumnManagerModal
          modalOpen={columnManagerOpen}
          cancelSetColumns={handleCancelManageColumns}
          columns={visibleColumns}
          setColumns={handleSetVisibleColumns}
        />
      )}

      <StbLoader isLoading={!isCustomerView && isLoadingOpportunities} />
    </div>
  );
};

export default OppTable;